import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Users, FileText, BookOpen, TrendingUp, RefreshCw,
  BarChart3, Newspaper, Sparkles, Layout,
  Trophy, BookMarked, MessageCircle, MessageCircleQuestion,
  UserCog, ClipboardList, Coins, ArrowLeft, Send, UserX, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [myPerms, setMyPerms] = useState([]);
  const [commentStats, setCommentStats] = useState(null);
  const [inactiveSummary, setInactiveSummary] = useState(null);

  const role = user?.admin_role || '';

  useEffect(() => {
    if (!user?.is_admin) { navigate('/login'); return; }
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [statsRes, analyticsRes, permsRes, commentsRes, inactiveRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/analytics').catch(() => ({ data: null })),
        api.get('/admin/my-permissions').catch(() => ({ data: { permissions: [] } })),
        api.get('/admin/comment-stats').catch(() => ({ data: null })),
        api.get('/admin/inactive-readers/summary').catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
      setMyPerms(permsRes.data?.permissions || []);
      setCommentStats(commentsRes.data);
      setInactiveSummary(inactiveRes.data);
    } catch { toast.error('فشل تحميل الاحصائيات'); }
    finally { setLoading(false); }
  };

  const handleRecomputeCounters = async () => {
    if (!window.confirm('سيتم إعادة حساب عدادات كل القراء (الكتب، المقالات، الدراسات، الصفحات، النقاط) من التقارير المكتملة. المتابعة؟')) return;
    setRecomputing(true);
    try {
      const res = await api.post('/admin/recompute-all-counters');
      toast.success(`تم إعادة الحساب لـ ${res.data.processed} قارئ`);
      loadStats();
    } catch { toast.error('فشلت إعادة الحساب'); }
    finally { setRecomputing(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div></div>;

  const statCards = [
    { label: 'اجمالي القراء', value: stats?.total_readers || 0, sub: `${stats?.male_readers || 0} ذكور - ${stats?.female_readers || 0} اناث`, icon: Users, color: 'text-primary' },
    { label: 'اجمالي التقارير', value: stats?.total_reports || 0, sub: `${stats?.today_reports || 0} تقرير اليوم`, icon: FileText, color: 'text-secondary' },
    { label: 'اجمالي الصفحات', value: stats?.total_pages_read || 0, icon: TrendingUp, color: 'text-accent' },
    { label: 'المساقات', value: stats?.total_courses || 0, sub: `${stats?.total_enrollments || 0} اشتراك`, icon: BookOpen, color: 'text-primary' },
  ];

  const quickActions = [
    { id: 'registrations', label: 'طلبات التسجيل', icon: UserCog, path: '/admin/registrations', tone: 'primary' },
    { id: 'reports', label: 'التقارير القرائية', icon: ClipboardList, path: '/admin/reports', tone: 'secondary' },
    { id: 'points', label: 'النقاط والإنجازات', icon: Coins, path: '/admin/points', tone: 'accent' },
    { id: 'articles', label: 'إدارة المقالات', icon: Newspaper, path: '/admin/articles', tone: 'primary' },
    { id: 'productions', label: 'مراجعة النتاجات', icon: FileText, path: '/admin/productions', tone: 'secondary' },
    { id: 'questions', label: 'أسئلة المساقات', icon: MessageCircleQuestion, path: '/admin/questions', tone: 'accent' },
    { id: 'comments', label: 'إدارة التعليقات', icon: MessageCircle, path: '/admin/comments', tone: 'primary' },
    { id: 'admin-accounts', label: 'الحسابات الإدارية', icon: UserCog, path: '/admin/admin-accounts', tone: 'secondary' },
  ].filter(a => myPerms.includes(a.id) || role === 'مطلق');

  const toneCls = (t) => ({
    primary: 'from-[#C19A6B]/15 to-[#C19A6B]/5 text-[#8B6636] hover:from-[#C19A6B]/25 hover:to-[#C19A6B]/10 border-[#C19A6B]/30',
    secondary: 'from-emerald-100/70 to-emerald-50/40 text-emerald-700 hover:from-emerald-100 hover:to-emerald-50 border-emerald-200',
    accent: 'from-[#5D4037]/12 to-[#5D4037]/4 text-[#5D4037] hover:from-[#5D4037]/20 hover:to-[#5D4037]/6 border-[#5D4037]/20',
  })[t] || '';

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-accent">لوحة التحكم</h1>
          <p className="text-muted-foreground text-base mt-2">مرحباً {user?.name} · <span className="text-sm bg-primary/10 text-primary px-2 py-0.5 rounded-full">{role}</span></p>
        </div>
        {role !== 'محرر' && (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={handleRecomputeCounters}
              disabled={recomputing}
              className="bg-primary hover:bg-primary/90"
              data-testid="recompute-counters-btn"
            >
              <RefreshCw className={`w-5 h-5 ml-2 ${recomputing ? 'animate-spin' : ''}`} />
              {recomputing ? 'جارٍ الحساب...' : 'إعادة حساب العدادات'}
            </Button>
          </div>
        )}
      </div>

      {role !== 'محرر' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((card, i) => {
            const Icon = card.icon;
            return (
              <Card key={i} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{card.label}</p>
                    <p className={`text-4xl font-bold ${card.color}`}>{card.value.toLocaleString('ar')}</p>
                    {card.sub && <p className="text-sm text-muted-foreground mt-2">{card.sub}</p>}
                  </div>
                  <Icon className={`w-10 h-10 ${card.color} opacity-20`} />
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Inactive-reader reminder — appears only when there is pending work */}
      {role !== 'محرر' && inactiveSummary && inactiveSummary.pending_total > 0 && (
        <div
          role="button"
          onClick={() => navigate('/admin/inactive')}
          className="relative overflow-hidden cursor-pointer rounded-2xl border border-rose-200 bg-gradient-to-l from-rose-50 via-white to-amber-50 p-5 md:p-6 hover:shadow-lg transition-all group"
          data-testid="inactive-reminder-card"
        >
          <div className="absolute inset-y-0 inset-inline-start-0 w-1.5 bg-rose-500" />
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-2xl bg-rose-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-7 h-7 text-rose-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg md:text-xl font-bold text-rose-700 flex items-center gap-2">
                <UserX className="w-5 h-5" />
                تنبيه: {inactiveSummary.pending_total} حساب بحاجة إلى متابعة
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {inactiveSummary.needs_contact > 0 && (
                  <span><b className="text-rose-700">{inactiveSummary.needs_contact}</b> لم يُتواصل معهم بعد</span>
                )}
                {inactiveSummary.needs_contact > 0 && inactiveSummary.needs_follow_up > 0 && <span className="mx-2">·</span>}
                {inactiveSummary.needs_follow_up > 0 && (
                  <span><b className="text-amber-700">{inactiveSummary.needs_follow_up}</b> بحاجة إلى متابعة (مضى على تواصلك أكثر من {inactiveSummary.thresholds?.follow_up_days} يوماً)</span>
                )}
              </p>
              {inactiveSummary.top?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {inactiveSummary.top.slice(0, 4).map((t) => (
                    <span key={t.reader_number} className="text-[11px] bg-white border border-rose-200 text-rose-800 px-2 py-1 rounded-full">
                      {t.name}
                      {t.inactive_days != null && t.inactive_days < 90000 && (
                        <span className="text-rose-500 mr-1"> · {t.inactive_days}ي</span>
                      )}
                    </span>
                  ))}
                  {inactiveSummary.top.length > 4 && (
                    <span className="text-[11px] text-muted-foreground px-1 py-1">+المزيد</span>
                  )}
                </div>
              )}
            </div>
            <div className="shrink-0 flex items-center gap-1 bg-rose-600 text-white px-4 py-2.5 rounded-xl group-hover:bg-rose-700 transition-colors">
              <span className="text-sm font-semibold">افتح المتابعة</span>
              <ArrowLeft className="w-4 h-4" />
            </div>
          </div>
        </div>
      )}

      {/* Analytics Section */}
      {role !== 'محرر' && analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Daily Reports Chart */}
          <Card className="p-6 lg:col-span-2" data-testid="daily-reports-chart">
            <h3 className="text-lg font-bold text-accent mb-6 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" /> التقارير اليومية (آخر 7 أيام)
            </h3>
            {analytics.daily_reports?.length > 0 ? (() => {
              const maxCount = Math.max(...analytics.daily_reports.map(d => d.count), 1);
              const gridLines = [0.25, 0.5, 0.75, 1].map(f => Math.round(maxCount * f));
              return (
                <div className="relative">
                  {/* Grid lines */}
                  <div className="absolute inset-0 flex flex-col justify-between pointer-events-none" style={{bottom: '28px', top: '24px'}}>
                    {gridLines.reverse().map((val, i) => (
                      <div key={i} className="flex items-center gap-2 w-full">
                        <span className="text-[10px] text-muted-foreground w-6 text-left shrink-0">{val}</span>
                        <div className="flex-1 border-b border-dashed border-gray-100" />
                      </div>
                    ))}
                  </div>
                  {/* Bars */}
                  <div className="flex items-end gap-4 h-56 pr-8 relative z-10">
                    {analytics.daily_reports.map((d, i) => {
                      const pct = (d.count / maxCount) * 100;
                      const dayName = new Date(d.date).toLocaleDateString('ar', { weekday: 'short' });
                      const dayNum = new Date(d.date).getDate();
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end">
                          <span className="text-base font-bold text-primary">{d.count || 0}</span>
                          <div className="w-full max-w-[56px] rounded-t-xl transition-all duration-500 bg-gradient-to-t from-primary to-primary/60"
                            style={{ height: `${Math.max(pct, 6)}%` }} />
                          <div className="text-center mt-1">
                            <p className="text-xs font-semibold text-accent">{dayName}</p>
                            <p className="text-[10px] text-muted-foreground">{dayNum}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })() : <p className="text-muted-foreground text-sm">لا توجد بيانات</p>}
          </Card>

          {/* Top Readers */}
          <Card className="p-6" data-testid="top-readers">
            <h3 className="text-lg font-bold text-accent mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" /> أنشط القراء هذا الأسبوع
            </h3>
            {analytics.top_readers?.length > 0 ? (
              <div className="space-y-3">
                {analytics.top_readers.slice(0, 5).map((r, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-gray-100 text-gray-600' : i === 2 ? 'bg-orange-100 text-orange-600' : 'bg-gray-50 text-gray-500'}`}>
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-accent truncate">{r.name || r.reader_number}</p>
                      <p className="text-[11px] text-muted-foreground">{r.count} تقرير · {r.pages} صفحة</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-muted-foreground text-sm">لا توجد تقارير هذا الأسبوع</p>}
          </Card>
        </div>
      )}

      {/* Top Books */}
      {role !== 'محرر' && analytics?.top_books?.length > 0 && (
        <Card className="p-6" data-testid="top-books">
          <h3 className="text-lg font-bold text-accent mb-4 flex items-center gap-2">
            <BookMarked className="w-5 h-5 text-primary" /> أكثر الكتب قراءة
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {analytics.top_books.slice(0, 10).map((b, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-primary/[0.03] border border-primary/10">
                <span className="text-lg font-bold text-primary/40">{i + 1}</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-accent truncate">{b.title}</p>
                  <p className="text-[11px] text-muted-foreground">{b.count} تقرير</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Comment Stats Section */}
      {role !== 'محرر' && commentStats && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="comment-stats">
          {/* Total + Recent Comments */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-accent mb-4 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-emerald-500" /> إحصائيات التعليقات
            </h3>
            <div className="text-center mb-4">
              <p className="text-5xl font-bold text-emerald-600">{commentStats.total_comments}</p>
              <p className="text-sm text-muted-foreground mt-1">إجمالي التعليقات</p>
            </div>
            {commentStats.recent_comments?.length > 0 && (
              <div className="space-y-2.5 border-t pt-3">
                <p className="text-xs font-semibold text-muted-foreground">آخر التعليقات</p>
                {commentStats.recent_comments.slice(0, 3).map((c, i) => (
                  <div key={i} className="p-2.5 rounded-lg bg-emerald-50/50 border border-emerald-100">
                    <p className="text-xs font-semibold text-accent">{c.reader_name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{c.text}</p>
                    <p className="text-[10px] text-emerald-600 mt-0.5">{c.article_title}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Most Commented Articles */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-accent mb-4 flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-blue-500" /> أكثر المقالات تعليقاً
            </h3>
            {commentStats.top_articles?.length > 0 ? (
              <div className="space-y-3">
                {commentStats.top_articles.slice(0, 5).map((a, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i === 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-50 text-gray-500'}`}>
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-accent truncate">{a.title}</p>
                      <p className="text-[11px] text-muted-foreground">{a.count} تعليق</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-muted-foreground text-sm">لا توجد تعليقات بعد</p>}
          </Card>

          {/* Most Active Commenters */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-accent mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-500" /> أنشط المعلقين
            </h3>
            {commentStats.top_commenters?.length > 0 ? (
              <div className="space-y-3">
                {commentStats.top_commenters.slice(0, 5).map((c, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i === 0 ? 'bg-violet-100 text-violet-700' : 'bg-gray-50 text-gray-500'}`}>
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-accent truncate">{c.name || c.reader_number}</p>
                      <p className="text-[11px] text-muted-foreground">{c.count} تعليق</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-muted-foreground text-sm">لا توجد تعليقات بعد</p>}
          </Card>
        </div>
      )}

      {quickActions.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-accent">اختصارات سريعة</h3>
            <p className="text-xs text-muted-foreground">القوائم الكاملة متاحة في الشريط الجانبي</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {quickActions.map((a) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.id}
                  onClick={() => navigate(a.path)}
                  data-testid={`goto-${a.id}`}
                  className={`group relative flex items-center gap-3 p-4 rounded-2xl border bg-gradient-to-br ${toneCls(a.tone)} transition-all hover:-translate-y-0.5 hover:shadow-md text-right`}
                >
                  <div className="w-11 h-11 rounded-xl bg-white/70 flex items-center justify-center shadow-sm shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-semibold flex-1">{a.label}</span>
                  <ArrowLeft className="w-4 h-4 opacity-40 group-hover:opacity-100 group-hover:-translate-x-0.5 transition-all" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
